import { ExpressionValueType } from "../../../../../rule-engine-core/Expressions/ExpressionValueType";
import { IfThenExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import { summarizeIfThenExpression } from "../../appglueExpressionSummary";
import {
    createRuleSummaryPresentation,
    type RuleIntegrationEvaluationContext,
    type RuleSummaryPresenterPort,
    type RuleSummaryValue,
} from "../../ruleIntegrationTypes";

type NativeStatementRuleActionLabel =
    | string
    | ((config: StatementRuleConfiguration, context?: RuleIntegrationEvaluationContext) => string | undefined);

export type NativeStatementRuleSummaryPresenterParams = {
    actionLabel?: NativeStatementRuleActionLabel;
};

const resolveActionLabel = (
    actionLabel: NativeStatementRuleActionLabel | undefined,
    config: StatementRuleConfiguration,
    context?: RuleIntegrationEvaluationContext,
): string | undefined => (typeof actionLabel === "function" ? actionLabel(config, context) : actionLabel);

export const createNativeStatementRuleSummaryPresenter = ({
    actionLabel,
}: NativeStatementRuleSummaryPresenterParams = {}): RuleSummaryPresenterPort<StatementRuleConfiguration> => ({
    renderSummary: (config, context) => {
        if (config.expression.valueType === ExpressionValueType.VALUE) {
            return createRuleSummaryPresentation({
                status: "complete",
                ast: {
                    kind: "rule_summary",
                    statements: [{ kind: "result", value: config.expression.value as RuleSummaryValue }],
                },
                sources: ["native_appglue_expression"],
            });
        }

        const expression = config.expression.expression;
        if (!(expression instanceof IfThenExpression)) {
            return createRuleSummaryPresentation({
                status: "unsupported",
                lines: ["APPGLUE native expression requires the native editor for full logic"],
                sources: ["native_appglue_expression"],
                warnings: ["Native statement summary supports IfThenExpression statement rules only."],
            });
        }

        const summary = summarizeIfThenExpression(expression);
        const resolvedActionLabel = resolveActionLabel(actionLabel, config, context);

        return createRuleSummaryPresentation({
            status: summary.covered ? "complete" : "unsupported",
            ast: {
                kind: "rule_summary",
                statements: [
                    ...summary.ast.statements,
                    ...(resolvedActionLabel ? [{ kind: "action" as const, label: resolvedActionLabel }] : []),
                ],
            },
            sources: resolvedActionLabel
                ? ["native_appglue_expression", "adapter_routing"]
                : ["native_appglue_expression"],
            warnings: summary.warnings,
        });
    },
});
